'use client';

import { forwardRef } from 'react';

import {
  Toaster as ChakraToaster,
  HTMLChakraProps,
  Icon,
  Portal,
  Spinner,
  Stack,
  Toast,
  createToaster,
  useToastStyles,
} from '@chakra-ui/react';
import { Cancel, CheckCircle, Error, Warning } from '@m/icons';

const iconMap: Record<string, { color: string; icon: React.ElementType }> = {
  error: {
    icon: Error,
    color: 'fill.icon.badge.negative',
  },
  neutral: {
    icon: Cancel,
    color: 'fill.icon.badge.neutral',
  },
  success: {
    icon: CheckCircle,
    color: 'fill.icon.badge.positive',
  },
  warning: {
    icon: Warning,
    color: 'fill.icon.badge.warning',
  },
};

export interface ToastIndicatorProps extends HTMLChakraProps<'svg'> {
  type?: keyof typeof iconMap;
}

export const CustomToastIndicator = forwardRef<SVGSVGElement, ToastIndicatorProps>(
  ({ type, ...props }, ref) => {
    const styles = useToastStyles();

    const Component = type ? iconMap[type].icon : null;
    if (!Component) return null;

    return (
      <Icon
        ref={ref}
        {...props}
        css={[styles.indicator['@layer recipes'], props.css]}
        as={Component}
        color={type ? iconMap[type].color : 'fill.badge.neutral'}
      />
    );
  },
);

export const toaster = createToaster({
  placement: 'top',
  pauseOnPageIdle: true,
});

export const Toaster = () => (
  <Portal>
    <ChakraToaster toaster={toaster} insetInline={{ mdDown: '4' }}>
      {(toast) => (
        <Toast.Root width={{ md: 'sm' }} justifyContent="center">
          {toast.type === 'loading' ? (
            <Spinner size="sm" color="blue.solid" />
          ) : (
            <CustomToastIndicator type={toast.type} />
          )}
          <Stack gap="1" flex="1" maxWidth="100%" alignItems="center" textAlign="center">
            {toast.title && <Toast.Title>{toast.title}</Toast.Title>}
            {toast.description && <Toast.Description>{toast.description}</Toast.Description>}
          </Stack>
          {toast.action && <Toast.ActionTrigger>{toast.action.label}</Toast.ActionTrigger>}
          {toast.meta?.closable && <Toast.CloseTrigger />}
        </Toast.Root>
      )}
    </ChakraToaster>
  </Portal>
);
